import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  SimpleGrid,
  HStack,
  Text,
  Icon,
  Box,
  Badge,
  useToast,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuBuilding2, LuSend } from 'react-icons/lu'
import { EnterpriseFormData } from '../../types/subscription'
import { CHROME_HEADER_GRADIENT } from '../../theme/chrome'

interface EnterpriseFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: EnterpriseFormData) => Promise<void>
}

const inputStyles = {
  borderRadius: 'lg',
  bg: 'gray.50',
  border: '1px solid',
  borderColor: 'gray.200',
  fontSize: 'sm',
  _focus: { bg: 'white', borderColor: 'purple.400', boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)' },
  _hover: { borderColor: 'gray.300' },
}

export default function EnterpriseFormModal({ isOpen, onClose, onSubmit }: EnterpriseFormModalProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<EnterpriseFormData>({
    companyName: '',
    contactName: '',
    email: '',
    phoneNumber: '',
    teamSize: '',
    industry: '',
    needs: '',
    additionalInfo: '',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async () => {
    if (!formData.companyName || !formData.contactName || !formData.email || !formData.teamSize) {
      toast({ title: 'Champs requis manquants', status: 'error', duration: 3000 })
      return
    }
    setIsSubmitting(true)
    try {
      await onSubmit(formData)
      toast({
        title: 'Demande envoyée',
        description: 'Notre équipe vous contactera sous 24h.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      onClose()
      setFormData({ companyName: '', contactName: '', email: '', phoneNumber: '', teamSize: '', industry: '', needs: '', additionalInfo: '' })
    } catch (_e) {
      toast({ title: "Erreur lors de l'envoi", status: 'error', duration: 5000 })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
      <ModalContent borderRadius="2xl" overflow="hidden" boxShadow="2xl">

        {/* Header gradient */}
        <ModalHeader bgGradient={CHROME_HEADER_GRADIENT} py={5} px={6}>
          <HStack spacing={3}>
            <Box bg="whiteAlpha.200" p={2} borderRadius="lg">
              <Icon as={LuBuilding2} boxSize={5} color="white" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text color="white" fontWeight="bold" fontSize="lg">Formule Institution</Text>
              <Text color="whiteAlpha.800" fontSize="xs" fontWeight="normal">
                Sur devis · Réponse sous 24h
              </Text>
            </VStack>
            <Badge ml="auto" bg="whiteAlpha.200" color="white" borderRadius="full" px={2.5} py={1} fontSize="2xs">
              Sur devis
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" top={4} right={4} />

        <ModalBody py={6} px={6}>
          <VStack spacing={5} align="stretch">

            {/* Établissement + Contact */}
            <SimpleGrid columns={2} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                  Établissement *
                </FormLabel>
                <Input
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="CHU de Nancy"
                  {...inputStyles}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                  Nom du contact *
                </FormLabel>
                <Input
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  placeholder="Dr. Martin Dupont"
                  {...inputStyles}
                />
              </FormControl>
            </SimpleGrid>

            {/* Email + Téléphone */}
            <SimpleGrid columns={2} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                  Email professionnel *
                </FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="m.dupont@chu-nancy.fr"
                  {...inputStyles}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                  Téléphone
                </FormLabel>
                <Input
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="+33 3 83 85 00 00"
                  {...inputStyles}
                />
              </FormControl>
            </SimpleGrid>

            {/* Taille équipe + Secteur */}
            <SimpleGrid columns={2} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                  Taille de l'équipe *
                </FormLabel>
                <Select name="teamSize" value={formData.teamSize} onChange={handleChange} {...inputStyles}>
                  <option value="">Sélectionner</option>
                  <option value="1-10">1 – 10 professionnels</option>
                  <option value="11-50">11 – 50 professionnels</option>
                  <option value="51-200">51 – 200 professionnels</option>
                  <option value="201-500">201 – 500 professionnels</option>
                  <option value="500+">500+ professionnels</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                  Secteur
                </FormLabel>
                <Select name="industry" value={formData.industry} onChange={handleChange} {...inputStyles}>
                  <option value="">Sélectionner</option>
                  <option value="hospital">CHU / Hôpital public</option>
                  <option value="lab">Laboratoire d'analyses</option>
                  <option value="research">Centre de recherche</option>
                  <option value="pharma">Industrie pharmaceutique</option>
                  <option value="other">Autre</option>
                </Select>
              </FormControl>
            </SimpleGrid>

            {/* Besoins */}
            <FormControl isRequired>
              <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                Vos besoins *
              </FormLabel>
              <Textarea
                name="needs"
                value={formData.needs}
                onChange={handleChange}
                placeholder="Décrivez vos cas d'usage, les modules souhaités, les contraintes d'intégration (SIH, FHIR, HDS)..."
                rows={3}
                resize="none"
                borderRadius="lg"
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                fontSize="sm"
                _focus={{ bg: 'white', borderColor: 'purple.400', boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)' }}
                _hover={{ borderColor: 'gray.300' }}
              />
            </FormControl>

          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100" px={6} py={4} gap={3}>
          <Text fontSize="xs" color="gray.400" flex={1}>
            * Champs obligatoires
          </Text>
          <Button variant="ghost" size="sm" onClick={onClose} colorScheme="gray">
            Annuler
          </Button>
          <Button
            size="sm"
            leftIcon={<Icon as={LuSend} />}
            bgGradient="linear(to-r, purple.600, indigo.500)"
            color="white"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Envoi..."
            borderRadius="lg"
            px={5}
            _hover={{ bgGradient: 'linear(to-r, purple.700, indigo.600)', transform: 'translateY(-1px)', boxShadow: 'md' }}
            transition="all 0.2s"
          >
            Envoyer la demande
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
